import * as Sentry from "@sentry/react";
import { config } from "./env";
import { SENTRY_DSN } from "./sentryDsn";

const EXPECTED_GRAPHQL_ERROR_CODES = [
	"UNAUTHENTICATED",
	"FORBIDDEN",
	"BAD_USER_INPUT",
];

const EXPECTED_GRAPHQL_ERROR_MESSAGES = [
	"not authenticated",
	"user not found in database",
	"access denied to organization",
	"employee access required",
];

export function isExpectedGraphQLError(error: {
	message: string;
	extensions?: Record<string, unknown>;
}): boolean {
	const code = error.extensions?.code as string | undefined;
	if (code && EXPECTED_GRAPHQL_ERROR_CODES.includes(code)) {
		return true;
	}
	const lowerMessage = error.message.toLowerCase();
	return EXPECTED_GRAPHQL_ERROR_MESSAGES.some((msg) =>
		lowerMessage.includes(msg),
	);
}

export function initSentry() {
	if (!config.isProduction) return;

	Sentry.init({
		dsn: SENTRY_DSN,
		environment: "production",
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				maskAllText: true,
				blockAllMedia: true,
			}),
		],
		tracesSampleRate: 0.1,
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
		ignoreErrors: [
			"ResizeObserver loop",
			"Non-Error promise rejection",
			"Load failed",
			"Failed to fetch",
		],
		denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
		beforeSend(event) {
			if (event.request?.url) {
				try {
					const url = new URL(event.request.url);
					url.search = "";
					event.request.url = url.toString();
				} catch {
					// URL parsing failed, leave as-is
				}
			}
			return event;
		},
	});
}

export function setSentryUser(user: { id: string; email?: string } | null) {
	if (user) {
		Sentry.setUser({ id: user.id, email: user.email });
	} else {
		Sentry.setUser(null);
	}
}

export { Sentry };
